import type React from 'react';
import styles from './Icon.module.css';
import chatIcon from '../../../assets/icons/chat.svg';
import chatNewIcon from '../../../assets/icons/chat-new.svg';
import assistantsIcon from '../../../assets/icons/assistants.svg';
import historyIcon from '../../../assets/icons/history.svg';
import closeIcon from '../../../assets/icons/close.svg';
import refreshIcon from '../../../assets/icons/refresh.svg';
import expandIcon from '../../../assets/icons/expand.svg';
import sendIcon from '../../../assets/icons/send.svg';
import pinIcon from '../../../assets/icons/pin.svg';
import pinUnpinIcon from '../../../assets/icons/pin-unpin.svg';
import devxgptLogo from '../../../assets/icons/DevXGPT-Logo.svg';
import pepsiLogo from '../../../assets/icons/Pepsi-Logo-White.svg';
import pepsiNewLogo from '../../../assets/icons/PepsiCo-Logo-New-WhiteBg-Small.jpg';
import openInNew from '../../../assets/icons/open-in-new.svg';


export type IconName =
  | 'send'
  | 'close'
  | 'refresh'
  | 'expand'
  | 'chat'
  | 'chat-new'
  | 'history'
  | 'assistants'
  | 'devxgpt-logo'
  | 'pepsi-logo'
  | 'pepsi-logo-new'
  | 'pin'
  | 'pin-unpin'
  | 'open-in-new'
  | 'search'
  | 'delete'
  | 'arrow-right'
  | 'user';

export interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
  'aria-hidden'?: boolean;
}

const iconMap: Record<IconName, string | React.FC<{ size: number }>> = {
  send: sendIcon,
  close: closeIcon,
  refresh: refreshIcon,
  expand: expandIcon,
  chat: chatIcon,
  'chat-new': chatNewIcon,
  history: historyIcon,
  assistants: assistantsIcon,
  'devxgpt-logo': devxgptLogo,
  'pepsi-logo': pepsiLogo,
  'pepsi-logo-new': pepsiNewLogo,
  pin: pinIcon,
  'pin-unpin': pinUnpinIcon,
  'open-in-new': openInNew,

  search: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <circle cx="11" cy="11" r="8" strokeWidth="2" />
      <path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),

  delete: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path
        d="M4 12C4 12.55 4.45 13 5 13H11C11.55 13 12 12.55 12 12V5H4V12ZM5 6H11V12H5V6ZM10.5 2.5L9.5 1.5H6.5L5.5 2.5H3V4H13V2.5H10.5Z"
        fill="currentColor"
      />
    </svg>
  ),

  'arrow-right': ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path
        d="M6 12L10 8L6 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),

  user: ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 5C13.66 5 15 6.34 15 8C15 9.66 13.66 11 12 11C10.34 11 9 9.66 9 8C9 6.34 10.34 5 12 5ZM12 19.2C9.5 19.2 7.29 17.92 6 15.98C6.03 13.99 10 12.9 12 12.9C13.99 12.9 17.97 13.99 18 15.98C16.71 17.92 14.5 19.2 12 19.2Z"
        fill="currentColor"
      />
    </svg>
  ),
};

export const Icon: React.FC<IconProps> = ({
  name,
  size = 20,
  className = '',
  'aria-hidden': ariaHidden = true,
}) => {
  const iconSource = iconMap[name];

  if (!iconSource) {
    console.warn(`Icon "${name}" not found`);
    return null;
  }

  return (
    <span className={`${styles.icon} ${className}`} aria-hidden={ariaHidden}>
      {typeof iconSource === 'string' ? (
        <img src={iconSource} alt="" width={size} height={size} />
      ) : (
        iconSource({ size })
      )}
    </span>
  );
};
